<template>
  <!-- Origem Flow: extras da toolbar da tabela "Todas as tarefas" (espelho
       agregado). Só aparece nessa tabela: agrupar por Status/Tabela, Modo
       eu e a sincronização de todas as tabelas com o Google Calendar. -->
  <li v-if="isAllTasks && !isPublicView" class="header__filter-item">
    <div class="all-tasks-header">
      <div v-if="canGroup" class="all-tasks-header__segment">
        <a
          v-for="option in groupOptions"
          :key="option.key"
          class="all-tasks-header__segment-link"
          :class="{
            'all-tasks-header__segment-link--active':
              currentGroup === option.key,
          }"
          @click="setGroup(option.key)"
        >
          {{ option.label }}
        </a>
      </div>
      <a
        v-if="assigneeField"
        class="header__filter-link"
        :class="{ 'active active--purple': meMode }"
        @click="toggleMeMode()"
      >
        <i class="header__filter-icon iconoir-user"></i>
        <span class="header__filter-name">{{
          $t('allTasksHeader.meMode')
        }}</span>
      </a>
      <span ref="gcalAllLink">
        <a
          class="header__filter-link"
          :class="{ 'active active--purple': gcalAllSynced }"
          @click="openGcalPanel()"
        >
          <i class="header__filter-icon iconoir-google"></i>
          <span class="header__filter-name">{{
            $t('allTasks.googleCalendar')
          }}</span>
        </a>
      </span>
      <Context
        ref="gcalAllContext"
        overflow-scroll
        max-height-if-outside-viewport
      >
        <div class="gcal-sync-panel">
          <div class="gcal-sync-panel__title">
            {{ $t('allTasks.googleTitle') }}
          </div>
          <template v-if="!gcalAll.accountConnected || !gcalAll.active">
            <p class="gcal-sync-panel__text">
              {{ $t('allTasks.googleExplanation') }}
            </p>
            <Button
              type="primary"
              :loading="gcalAll.loading"
              @click="connectAllViaOauth()"
            >
              {{ $t('allTasks.googleConnectAll') }}
            </Button>
          </template>
          <template v-else>
            <p class="gcal-sync-panel__text">
              {{
                $t('allTasks.googleSyncedCount', {
                  connected: connectedTaskTables,
                  total: taskTableIds.length,
                })
              }}
            </p>
            <Button
              v-if="!gcalAllSynced"
              type="primary"
              :loading="gcalAll.loading"
              @click="connectAllTables()"
            >
              {{ $t('allTasks.googleConnectAll') }}
            </Button>
            <p
              v-else
              class="gcal-sync-panel__text gcal-sync-panel__text--muted"
            >
              <i class="iconoir-check-circle gcal-sync-panel__ok"></i>
              {{ $t('allTasks.googleAllSynced') }}
            </p>
            <p class="gcal-sync-panel__text gcal-sync-panel__text--muted">
              {{ $t('allTasks.googleDisconnectHint') }}
            </p>
          </template>
        </div>
      </Context>
    </div>
  </li>
</template>

<script>
import { notifyIf } from '@baserow/modules/core/utils/error'
import GoogleCalendarService from '@baserow/modules/core/services/googleCalendar'

export default {
  name: 'AllTasksHeader',
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    isPublicView: {
      type: Boolean,
      required: false,
      default: false,
    },
    storePrefix: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      busy: false,
      gcalAll: {
        accountConnected: false,
        active: false,
        connections: [],
        loading: false,
      },
    }
  },
  computed: {
    mirror() {
      return this.table.all_tasks || null
    },
    isAllTasks() {
      return this.mirror !== null
    },
    mirrorFieldIds() {
      return this.mirror ? this.mirror.fields || {} : {}
    },
    statusField() {
      return this.fieldById(this.mirrorFieldIds.status)
    },
    tableField() {
      return this.fieldById(this.mirrorFieldIds.table)
    },
    assigneeField() {
      return this.fieldById(this.mirrorFieldIds.assignee)
    },
    groupOptions() {
      return [
        { key: 'status', label: this.$t('allTasksHeader.groupStatus') },
        { key: 'table', label: this.$t('allTasksHeader.groupTable') },
      ]
    },
    canGroup() {
      return (
        this.statusField &&
        this.tableField &&
        ['grid', 'gallery', 'kanban'].includes(this.view.type)
      )
    },
    currentGroup() {
      const fieldId =
        this.view.type === 'kanban'
          ? this.view.single_select_field
          : (this.view.group_bys || [])[0]?.field
      if (fieldId === this.statusField?.id) {
        return 'status'
      }
      if (fieldId === this.tableField?.id) {
        return 'table'
      }
      return null
    },
    userId() {
      return this.$store.getters['auth/getUserId']
    },
    meFilter() {
      if (!this.assigneeField) {
        return null
      }
      return (this.view.filters || []).find(
        (filter) =>
          filter.field === this.assigneeField.id &&
          filter.type === 'multiple_collaborators_has' &&
          String(filter.value) === String(this.userId)
      )
    },
    meMode() {
      return !!this.meFilter
    },
    // As tabelas de tarefas do espaço (opções do campo Tabela do espelho).
    taskTableIds() {
      return this.gcalAll.taskTableIds || []
    },
    connectedTaskTables() {
      const connected = new Set(this.gcalAll.connections.map((c) => c.table_id))
      return this.taskTableIds.filter((id) => connected.has(id)).length
    },
    gcalAllSynced() {
      if (!this.gcalAll.accountConnected || !this.gcalAll.active) {
        return false
      }
      return (
        this.taskTableIds.length > 0 &&
        this.connectedTaskTables === this.taskTableIds.length
      )
    },
  },
  mounted() {
    if (this.isAllTasks) {
      this.refreshGcalAllStatus()
    }
  },
  methods: {
    fieldById(id) {
      if (!id) {
        return null
      }
      return this.fields.find((field) => field.id === id) || null
    },
    refreshTable() {
      this.$bus.$emit('table-refresh', { tableId: this.table.id })
    },
    async setGroup(key) {
      if (this.busy || this.currentGroup === key) {
        return
      }
      const field = key === 'status' ? this.statusField : this.tableField
      this.busy = true
      try {
        if (this.view.type === 'kanban') {
          await this.$store.dispatch('view/update', {
            view: this.view,
            values: { single_select_field: field.id },
          })
        } else {
          for (const groupBy of [...(this.view.group_bys || [])]) {
            await this.$store.dispatch('view/deleteGroupBy', {
              view: this.view,
              groupBy,
            })
          }
          // Na ordem das opções (Pendente → Cancelado; tabelas na ordem do
          // sidebar), não alfabética. O pill de agrupar deixa trocar.
          await this.$store.dispatch('view/createGroupBy', {
            view: this.view,
            values: { field: field.id, value: 'ASC', type: 'order' },
          })
        }
        this.refreshTable()
      } catch (error) {
        notifyIf(error, 'view')
      } finally {
        this.busy = false
      }
    },
    async toggleMeMode() {
      if (this.busy || !this.assigneeField) {
        return
      }
      this.busy = true
      try {
        if (this.meFilter) {
          await this.$store.dispatch('view/deleteFilter', {
            view: this.view,
            filter: this.meFilter,
          })
        } else {
          await this.$store.dispatch('view/createFilter', {
            field: this.assigneeField,
            view: this.view,
            values: {
              field: this.assigneeField.id,
              type: 'multiple_collaborators_has',
              value: String(this.userId),
            },
            emitEvent: false,
          })
        }
        this.refreshTable()
      } catch (error) {
        notifyIf(error, 'view')
      } finally {
        this.busy = false
      }
    },
    openGcalPanel() {
      this.$refs.gcalAllContext.toggle(
        this.$refs.gcalAllLink,
        'bottom',
        'left',
        4
      )
    },
    async refreshGcalAllStatus() {
      try {
        const { data } = await GoogleCalendarService(this.$client).status(
          null,
          this.mirror.workspace_id
        )
        this.gcalAll.accountConnected = data.account_connected
        this.gcalAll.active = data.active || false
        this.gcalAll.connections = data.connections || []
        this.gcalAll.taskTableIds = (data.task_table_ids || []).map((id) =>
          parseInt(id)
        )
      } catch (error) {
        // O chip segue como "não conectado".
      }
    },
    async connectAllViaOauth() {
      this.gcalAll.loading = true
      try {
        const { data } = await GoogleCalendarService(
          this.$client
        ).authUrlConnectAll(this.mirror.workspace_id, this.$route.path)
        window.location.href = data.auth_url
      } catch (error) {
        this.gcalAll.loading = false
        notifyIf(error)
      }
    },
    async connectAllTables() {
      this.gcalAll.loading = true
      try {
        const { data } = await GoogleCalendarService(
          this.$client
        ).connectAllTables(this.mirror.workspace_id)
        await this.refreshGcalAllStatus()
        this.$store.dispatch('toast/success', {
          title: this.$t('allTasks.googleConnectedToastTitle'),
          message: this.$t('allTasks.googleConnectedToast', {
            count: data.connected_table_ids.length,
          }),
        })
      } catch (error) {
        notifyIf(error)
      } finally {
        this.gcalAll.loading = false
      }
    },
  },
}
</script>
